import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  Collapse,
  InputBase,
} from "@mui/material";
import {
  Dashboard,
  LocalShipping,
  People,
  DirectionsBoat,
  Receipt,
  BarChart,
  InsertChartOutlined,
  IntegrationInstructions,
  WorkspacePremium,
  Settings,
  Menu as MenuIcon,
  Notifications,
  ExpandLess,
  ExpandMore,
  Logout,
  Article,
  Payments as PaymentsIcon,
  DirectionsCar,
  Email as EmailIcon, History,
  UploadFile,
  Inventory2,
  AttachMoney,
  CallMerge,
  AssignmentTurnedIn,
  QrCodeScanner,
  Checklist,
  Podcasts,
  LocalOffer,
  Search,
  Close,
  CardGiftcard,
} from "@mui/icons-material";

import ColorModeToggle from "../components/common/ColorModeToggle";
import { logoutUser } from "../redux/slices/authSlice";
import { selectUnreadCount, fetchNotifications } from "../redux/slices/notificationSlice";
import { fetchStatusDefinitions } from "../redux/slices/statusDefinitionsSlice";
import { fetchCurrencies } from "../redux/slices/currenciesSlice";
import { fetchSystemSettings } from "../redux/slices/systemSettingsSlice";
import usePermissions from "../hooks/usePermissions";
import usePresenceHeartbeat from "../hooks/usePresenceHeartbeat";

const SIDEBAR_W = 240;
const SIDEBAR_W_COL = 68;

const navSections = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", icon: <Dashboard />, path: "/admin" },
      { label: "Notifications", icon: <Notifications />, path: "/admin/notifications", badgeProp: true, permission: "view_notifications" },
    ],
  },
  {
    label: "Shipments",
    items: [
      { label: "All Shipments", icon: <LocalShipping />, path: "/admin/shipments", permission: "view_shipments" },
      { label: "Delivery Receipts", icon: <Receipt />, path: "/admin/delivery-receipts", permission: "view_delivery_receipts" },
      { label: "Unclaimed Packages", icon: <Inventory2 />, path: "/admin/unclaimed-packages", permission: "view_unclaimed_packages" },
      { label: "Dock Receipts", icon: <AssignmentTurnedIn />, path: "/admin/dock-receipts", permission: "manage_shipments" },
      { label: "Warehouse Scan", icon: <QrCodeScanner />, path: "/admin/scan", permission: "manage_shipments" },
      { label: "Item Tracking", icon: <Checklist />, path: "/admin/item-tracking", permission: "manage_shipments" },
      { label: "Ready to Sail", icon: <DirectionsBoat />, path: "/admin/ready-to-sail", permission: "manage_shipments" },
      { label: "Consolidations", icon: <CallMerge />, path: "/admin/consolidations", permission: "view_consolidations" },
    ],
  },
  {
    label: "Customs & Compliance",
    items: [
      { label: "Customs", icon: <Article />, path: "/admin/customs", permission: "view_customs" },
      { label: "Sailing Schedules", icon: <DirectionsBoat />, path: "/admin/sailing", permission: "view_sailing" },
      { label: "Upload BOL", icon: <UploadFile />, path: "/admin/bol-upload", permission: "upload_bol" },
      { label: "Containers", icon: <Inventory2 />, path: "/admin/containers", permission: "view_sailing" },
      { label: "Reference Codes", icon: <Article />, path: "/admin/codes", permission: "manage_customs" },
    ],
  },
  {
    label: "Vehicles",
    items: [
      { label: "Vehicle Imports", icon: <DirectionsCar />, path: "/admin/vehicles", permission: "view_vehicles" },
    ],
  },
  {
    label: "People",
    items: [
      { label: "Users Management", icon: <People />, path: "/admin/customers", permission: "view_users" },
      { label: "Live Traffic", icon: <Podcasts />, path: "/admin/live-traffic", permission: "view_users" },
      { label: "Referral Program", icon: <CardGiftcard />, path: "/admin/referrals", permission: "view_users" },
    ],
  },
  {
    label: "Finance",
    items: [
      { label: "Invoices", icon: <Receipt />, path: "/admin/invoices", permission: "view_invoices" },
      { label: "Generate Invoice", icon: <AttachMoney />, path: "/admin/generate-invoice", permission: "manage_invoices" },
      { label: "Payments", icon: <PaymentsIcon />, path: "/admin/payments", permission: "view_payments" },
      { label: "Expenses", icon: <DirectionsBoat />, path: "/admin/expenses", permission: "view_sailing" },
      { label: "Promos", icon: <LocalOffer />, path: "/admin/promos", permission: "manage_invoices" },
      { label: "Reports", icon: <BarChart />, path: "/admin/reports", permission: "view_reports" },
      { label: "Stats", icon: <InsertChartOutlined />, path: "/admin/stats", permission: "view_reports" },
    ],
  },
  {
    label: "Communications",
    items: [
      { label: "Email Templates", icon: <EmailIcon />, path: "/admin/email-templates", permission: "view_email_templates" },
      { label: "Email Logs", icon: <History />, path: "/admin/email-logs", permission: "view_email_logs" },
    ],
  },
  {
    label: "Rates & Templates",
    items: [
      { label: "Invoice Rates", icon: <AttachMoney />, path: "/admin/invoice-rates", permission: "manage_settings" },
      { label: "Shopper Rates", icon: <AttachMoney />, path: "/admin/shopper-rates", permission: "manage_shopper_rates" },
      { label: "Delivery Receipt Template", icon: <Receipt />, path: "/admin/delivery-receipt-template", permission: "manage_settings" },
      { label: "Invoice Template", icon: <Receipt />, path: "/admin/invoice-template", permission: "manage_settings" },
      { label: "BOL / Manifest Templates", icon: <Article />, path: "/admin/customs-doc-templates", permission: "manage_settings" },
      { label: "Status Settings", icon: <Settings />, path: "/admin/status-settings", permission: "view_status_settings" },
      { label: "Integrations", icon: <IntegrationInstructions />, path: "/admin/integrations", permission: "manage_settings" },
      { label: "Upgrade Page", icon: <WorkspacePremium />, path: "/admin/upgrade-page", permission: "manage_settings" },
    ],
  },
];

// Moved OUTSIDE AdminLayout — this is the fix. Defined once at module load,
// not recreated on every render, so React keeps the same DOM tree (and
// therefore scroll position) across navigations. All data it needs now
// comes in via props instead of closures.
const SidebarContent = ({
  forceExpanded = false,
  collapsed,
  location,
  navigate,
  unreadCount,
  has,
  openSections,
  onToggleSection,
  navSearch,
  onNavSearchChange,
}) => {
  const isCollapsed = forceExpanded ? false : collapsed;
  const query = navSearch.trim().toLowerCase();

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2.25,
          py: 2.5,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/logos/Waypoint-Imports-Favicon-White.png"
          alt="Waypoint Imports"
          sx={{
            width: 44,
            height: 44,
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
        {!isCollapsed && (
          <Box>
            <Typography
              sx={{
                color: "white",
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              Waypoint
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.45)",
                fontSize: 9.5,
                letterSpacing: "0.5px",
                fontWeight: 500,
              }}
            >
              IMPORTS PORTAL
            </Typography>
          </Box>
        )}
      </Box>

      {/* Search — filters menu items across every section by label. Hidden
          in icon-only collapsed mode, same as the section headers, since
          there's no room for it there. */}
      {!isCollapsed && (
        <Box sx={{ px: 1.5, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.08)", }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              bgcolor: "rgba(255,255,255,0.07)",
              borderRadius: 1.5,
              px: 1.25,
              py: 0.625,
            }}
          >
            <Search sx={{ fontSize: 16, color: "rgba(255,255,255,0.4)" }} />
            <InputBase
              value={navSearch}
              onChange={(e) => onNavSearchChange(e.target.value)}
              placeholder="Search menu…"
              // name/id intentionally NOT "search" or anything username-like
              // — Chrome's password-manager autofill groups the nearest
              // preceding text input on the page as a login form's
              // "username" field when a password field elsewhere lacks its
              // own autoComplete hint (see AdminSettings.jsx's Security tab
              // fields), which was filling the admin's saved email in here.
              // autoComplete="off" plus a non-matching name stops that
              // heuristic from picking this input.
              name="admin-nav-search"
              autoComplete="off"
              inputProps={{ autoComplete: "off" }}
              sx={{
                flex: 1,
                fontSize: 12.5,
                color: "white",
                "& input::placeholder": { color: "rgba(255,255,255,0.4)", opacity: 1 },
              }}
            />
            {navSearch && (
              <IconButton size="small" onClick={() => onNavSearchChange("")} sx={{ p: 0.25 }}>
                <Close sx={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {/* Nav */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        {navSections.map((section) => {
          const permittedItems = section.items.filter((item) => !item.permission || has(item.permission));
          const visibleItems = query
            ? permittedItems.filter((item) => item.label.toLowerCase().includes(query))
            : permittedItems;
          if (visibleItems.length === 0) return null;

          // Section-level collapse only applies in the full (non-icon-only)
          // sidebar — when the whole sidebar is collapsed to icons there's
          // no header to click anyway, so every item just shows regardless
          // of the admin's per-section preference. A search in progress
          // also forces every matching section open, overriding whatever
          // the admin collapsed — otherwise a match could be hiding inside
          // a section they'd closed and never show up.
          const sectionOpen = isCollapsed || !!query || openSections[section.label] !== false;

          return (
            <Box key={section.label} sx={{ mb: 0.5 }}>
              {!isCollapsed && (
                <ListItemButton
                  onClick={() => !query && onToggleSection(section.label)}
                  sx={{
                    px: 2.25,
                    pt: 2,
                    pb: 0.75,
                    minHeight: 0,
                    "&:hover": { bgcolor: "transparent" },
                  }}
                  disableRipple
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 9.5,
                      fontWeight: 600,
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    {section.label}
                  </Typography>
                  {sectionOpen ? (
                    <ExpandLess sx={{ fontSize: 16, color: "rgba(255,255,255,0.3)" }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 16, color: "rgba(255,255,255,0.3)" }} />
                  )}
                </ListItemButton>
              )}
              <Collapse in={sectionOpen} timeout="auto" unmountOnExit={!isCollapsed}>
                {visibleItems.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== "/admin" &&
                      location.pathname.startsWith(item.path));

                  return (
                    <Tooltip
                      key={item.path}
                      title={isCollapsed ? item.label : ""}
                      placement="right"
                    >
                      <ListItemButton
                        onClick={() => navigate(item.path)}
                        selected={isActive}
                        sx={{
                          px: isCollapsed ? 0 : 2.25,
                          py: 1.125,
                          justifyContent: isCollapsed ? "center" : "flex-start",
                          position: "relative",
                          color: isActive ? "white" : "rgba(255,255,255,0.65)",
                          "&.Mui-selected": {
                            bgcolor: "rgba(255,255,255,0.12)",
                            color: "white",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 3,
                              background: "#0D629B",
                              borderRadius: "0 2px 2px 0",
                            },
                          },
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.07)",
                            color: "white",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: "inherit",
                            minWidth: isCollapsed ? 0 : 36,
                            "& svg": { fontSize: 19 },
                          }}
                        >
                          {item.badge || (item.badgeProp && unreadCount > 0) ? (
                            <Badge
                              badgeContent={
                                item.badgeProp ? unreadCount : item.badge
                              }
                              color="primary"
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: 9,
                                  height: 16,
                                  minWidth: 16,
                                },
                              }}
                            >
                              {item.icon}
                            </Badge>
                          ) : (
                            item.icon
                          )}
                        </ListItemIcon>
                        {!isCollapsed && (
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: 13,
                              fontWeight: isActive ? 600 : 500,
                            }}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  );
                })}
              </Collapse>
            </Box>
          )
        })}
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        {has("manage_settings", "manage_shopper_rates") && (
          <Tooltip title={isCollapsed ? "Settings" : ""} placement="right">
            <ListItemButton
              onClick={() => navigate("/admin/settings")}
              sx={{
                px: isCollapsed ? 0 : 2.25,
                py: 1.125,
                justifyContent: isCollapsed ? "center" : "flex-start",
                color: "rgba(255,255,255,0.65)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.07)", color: "white" },
              }}
            >
              <ListItemIcon
                sx={{ color: "inherit", minWidth: isCollapsed ? 0 : 36 }}
              >
                <Settings sx={{ fontSize: 19 }} />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const AdminLayout = () => {
  const { has } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = useSelector(selectUnreadCount);
  usePresenceHeartbeat();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navSearch, setNavSearch] = useState("");

  // Per-section sidebar collapse state (Overview, Shipments, Customs &
  // Compliance, etc.) — persisted so an admin's "keep only what I use
  // open" preference survives reloads/logins, not just this session.
  // Missing from the saved object (a section added after they last saved
  // prefs) defaults to open via the `!== false` check in SidebarContent.
  const [openSections, setOpenSections] = useState(() => {
    try {
      const saved = localStorage.getItem("adminSidebarOpenSections");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const toggleSection = (label) => {
    setOpenSections((prev) => {
      const next = { ...prev, [label]: prev[label] === false ? true : false };
      try {
        localStorage.setItem("adminSidebarOpenSections", JSON.stringify(next));
      } catch {
        /* storage unavailable — preference just won't persist */
      }
      return next;
    });
  };

  const sidebarW = collapsed ? SIDEBAR_W_COL : SIDEBAR_W;

  useEffect(() => {
    dispatch(fetchStatusDefinitions());
    dispatch(fetchCurrencies());
    dispatch(fetchSystemSettings());
  }, [dispatch]);

  // Badge in the sidebar needs live data — fetch on mount, then poll so it
  // doesn't go stale for an admin who stays on one page all day, without
  // needing a websocket.
  useEffect(() => {
    dispatch(fetchNotifications({ limit: 20 }));
    const interval = setInterval(() => dispatch(fetchNotifications({ limit: 20 })), 60000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate("/login");
  };

  return (
    // position: "fixed", inset: 0 — see the matching comment in
    // CustomerLayout.jsx; pins the whole shell to the viewport so the
    // window/body itself can never scroll, regardless of how tall any
    // page's content gets, leaving `.app-scroll-container` as the only
    // thing that scrolls.
    <Box className="admin-shell" sx={{ display: "flex", position: "fixed", inset: 0, overflow: "hidden" }}>
      {/* Desktop Sidebar */}
      <Drawer
        variant="permanent"
        className="no-print"
        sx={{
          width: sidebarW,
          flexShrink: 0,
          transition: "width 0.25s ease",
          "& .MuiDrawer-paper": {
            width: sidebarW,
            transition: "width 0.25s ease",
            overflowX: "hidden",
            bgcolor: "#001A3C",
          },
          display: { xs: "none", md: "block" },
        }}
      >
        <SidebarContent
          collapsed={collapsed}
          location={location}
          navigate={navigate}
          unreadCount={unreadCount}
          has={has}
          openSections={openSections}
          onToggleSection={toggleSection}
          navSearch={navSearch}
          onNavSearchChange={setNavSearch}
        />
      </Drawer>

      {/* Mobile Sidebar */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        className="no-print"
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_W, bgcolor: "#001A3C" },
        }}
      >
        <SidebarContent
          forceExpanded
          collapsed={collapsed}
          location={location}
          navigate={navigate}
          unreadCount={unreadCount}
          has={has}
          openSections={openSections}
          onToggleSection={toggleSection}
          navSearch={navSearch}
          onNavSearchChange={setNavSearch}
        />
      </Drawer>

      {/* Main */}
      <Box
        className="admin-main"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {/* AppBar */}
        <AppBar position="static" elevation={0} className="no-print">
          <Toolbar sx={{ minHeight: `60px !important`, px: 2.5, gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => {
                setCollapsed((p) => !p);
                setMobileOpen((p) => !p);
              }}
              sx={{ color: "text.secondary" }}
            >
              <MenuIcon />
            </IconButton>

            {/* `flex={1}` as a bare prop does nothing in this MUI version —
                only the `sx` prop is processed for Box's style props now
                (confirmed in @mui/system's styleFunctionSx, which no-ops
                unless `sx` is set). This spacer silently failed to push the
                icons right until it was wrapped in sx. */}
            <Box sx={{ flex: 1 }} />

            <ColorModeToggle />

            {/* Notification bell and Avatar/Profile menu removed — both
                already live in the sidebar (Notifications nav item, the
                Settings link in the sidebar footer for Profile). Logout is
                the only account-level action not already reachable from the
                sidebar, so it gets its own always-visible button here, same
                treatment as CustomerLayout.jsx. */}
            <Button
              size="small"
              onClick={handleLogout}
              startIcon={<Logout sx={{ fontSize: 18 }} />}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                color: "error.main",
                textTransform: "none",
                fontWeight: 600,
                px: 1.5,
              }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        {/* minHeight: 0 — see the matching comment in CustomerLayout.jsx;
            same flex-item-won't-shrink-below-content bug, same fix. */}
        <Box className="admin-content app-scroll-container" sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 3 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;